import { useState } from 'react';
import type { JumpLogic, JumpTarget, Question } from './types';
import { titleText } from './types';

type Props = {
  jumpLogic: JumpLogic | undefined;
  allQuestions: Question[];
  currentQid: string;
  onChange: (logic: JumpLogic | undefined) => void;
  onClose: () => void;
};

/** 下拉里展示的「跳转目标」选项 */
type Option = { value: string; label: string };

const KIND_LABEL: Record<JumpTarget['kind'], string> = {
  next: '下一题（顺序）',
  end: '末尾结束作答',
  submitInvalid: '直接提交无效卷',
  question: '跳到指定题',
};

/** 把 JumpTarget 编码成下拉里的 value 字符串（kind 或 `question:${qid}`） */
function encodeTarget(t: JumpTarget | undefined): string {
  if (!t) return '';
  if (t.kind === 'question') return `question:${t.qid}`;
  return t.kind;
}

/** 把下拉 value 解码成 JumpTarget；空字符串视为未选 */
function decodeTarget(v: string, currentQid: string, afterIds: string[]): JumpTarget | null {
  if (!v) return null;
  if (v === 'question:') return null;
  if (v.startsWith('question:')) {
    const qid = v.slice('question:'.length);
    if (!qid || qid === currentQid) return null;
    if (!afterIds.includes(qid)) return null;
    return { kind: 'question', qid };
  }
  if (v === 'next' || v === 'end' || v === 'submitInvalid') return { kind: v };
  return null;
}

/** 题号 + 题干（用于下拉里展示题名） */
function questionLabel(q: Question, displayIndex: number): string {
  // 0904 反馈：题干为空时不回落到 q.id（避免下拉里看到 Q1/Q2 这种 ID 串）
  const t = titleText(q.title);
  return t ? `第 ${displayIndex} 题 · ${t}` : `第 ${displayIndex} 题`;
}

export default function JumpLogicModal({ jumpLogic, allQuestions, currentQid, onChange, onClose }: Props) {
  const [draft, setDraft] = useState<JumpLogic>(() => ({
    byOption: jumpLogic?.byOption ? { ...jumpLogic.byOption } : undefined,
    unconditional: jumpLogic?.unconditional,
  }));

  // 当前题位置 → 之后的所有可见题 id（statement / pagebreak 不计 displayIndex 但可跳到）
  const currentIdx = allQuestions.findIndex((q) => q.id === currentQid);
  const afterQs = currentIdx >= 0 ? allQuestions.slice(currentIdx + 1) : [];
  const afterIds = afterQs.map((q) => q.id);

  // 显示题号（statement / pagebreak 不占序号；用 displayIndexMap 规则）
  const displayNumbers: Record<string, number> = (() => {
    let n = 0;
    const map: Record<string, number> = {};
    for (const q of allQuestions) {
      if (q.type !== 'statement' && q.type !== 'pagebreak') n += 1;
      map[q.id] = n;
    }
    return map;
  })();

  // 目标下拉选项：固定 3 种 + 当前题之后的每道题
  const targetOptions: Option[] = [
    { value: 'next', label: KIND_LABEL.next },
    { value: 'end', label: KIND_LABEL.end },
    { value: 'submitInvalid', label: KIND_LABEL.submitInvalid },
    ...afterQs.map((q) => ({ value: `question:${q.id}`, label: questionLabel(q, displayNumbers[q.id] ?? 0) })),
  ];

  const cur = allQuestions[currentIdx];
  const hasOptions = (cur?.options ?? []).length > 0 && ['single', 'multi', 'select'].includes(cur?.type ?? '');
  const options: { value: string }[] = (cur?.options ?? [])
    .map((o) => (typeof o === 'string' ? { value: o } : { value: o.value }))
    .filter((o) => typeof o.value === 'string' && o.value !== '');

  const byOptionOn = !!draft.byOption;
  const unconditionalOn = !!draft.unconditional;

  const toggleByOption = (on: boolean) => {
    setDraft((d) => {
      if (on) {
        // 开启：默认每选项都「下一题」
        const init: Record<string, JumpTarget> = {};
        for (const o of options) init[o.value] = d.byOption?.[o.value] ?? { kind: 'next' };
        return { ...d, byOption: init };
      }
      const { byOption: _drop, ...rest } = d;
      void _drop;
      return { ...rest, byOption: undefined };
    });
  };

  const toggleUnconditional = (on: boolean) => {
    setDraft((d) => ({
      ...d,
      unconditional: on ? (d.unconditional ?? { kind: 'next' }) : undefined,
    }));
  };

  const setByOptionTarget = (optValue: string, target: JumpTarget | null) => {
    setDraft((d) => {
      if (!d.byOption) return d;
      const next = { ...d.byOption };
      if (target) next[optValue] = target;
      else delete next[optValue];
      return { ...d, byOption: next };
    });
  };

  const setUnconditionalTarget = (target: JumpTarget | null) => {
    setDraft((d) => ({ ...d, unconditional: target ?? undefined }));
  };

  const handleConfirm = () => {
    // 清理空目标
    const cleanByOption: Record<string, JumpTarget> | undefined = (() => {
      if (!draft.byOption) return undefined;
      const out: Record<string, JumpTarget> = {};
      for (const [k, v] of Object.entries(draft.byOption)) {
        if (v) out[k] = v;
      }
      return Object.keys(out).length ? out : undefined;
    })();
    const cleanUnconditional: JumpTarget | undefined = draft.unconditional || undefined;
    if (!cleanByOption && !cleanUnconditional) {
      onChange(undefined);
    } else {
      onChange({ byOption: cleanByOption, unconditional: cleanUnconditional });
    }
    onClose();
  };

  // 当前题题号展示
  const curNo = displayNumbers[currentQid] ?? 0;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>跳题逻辑</h3>
          <button className="icon-btn" onClick={onClose} title="关闭">✕</button>
        </div>

        <div className="modal-body">
          <div className="hint" style={{ marginBottom: 8 }}>
            当前是「第 {curNo} 题 · {titleText(cur?.title) || '未命名题'}」。
            跳转目标只能选择题号在本题之后的题；可叠加按选项跳题 + 无条件跳题，无条件优先。
          </div>

          {/* 按选项跳题 */}
          {hasOptions ? (
            <div className="jump-section">
              <label className={`logic-op-pill ${byOptionOn ? 'is-on' : ''}`}>
                <input type="checkbox" checked={byOptionOn} onChange={(e) => toggleByOption(e.target.checked)} />
                按选项跳题 <span className="op-tag">按选项</span>
              </label>
              {byOptionOn && (
                <div className="logic-conds">
                  {options.map((o) => {
                    const cur = draft.byOption?.[o.value];
                    const value = encodeTarget(cur);
                    return (
                      <div key={o.value} className="logic-row">
                        <span className="jump-opt-label" title={o.value}>{o.value}</span>
                        <span className="jump-arrow">→</span>
                        <select
                          className="input"
                          value={value}
                          onChange={(e) => setByOptionTarget(o.value, decodeTarget(e.target.value, currentQid, afterIds))}
                        >
                          <option value="">未配置（顺序下一题）</option>
                          {targetOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="hint hint-bad" style={{ marginBottom: 8 }}>
              当前题型无选项，仅可配置「无条件跳题」。
            </div>
          )}

          {/* 无条件跳题 */}
          <div className="jump-section" style={{ marginTop: hasOptions ? 16 : 0 }}>
            <label className={`logic-op-pill ${unconditionalOn ? 'is-on' : ''}`}>
              <input type="checkbox" checked={unconditionalOn} onChange={(e) => toggleUnconditional(e.target.checked)} />
              无条件跳题 <span className="op-tag">答完即跳</span>
            </label>
            {unconditionalOn && (
              <div className="logic-conds">
                <div className="logic-row">
                  <span className="jump-opt-label">填写此题后</span>
                  <span className="jump-arrow">→</span>
                  <select
                    className="input"
                    value={encodeTarget(draft.unconditional)}
                    onChange={(e) => setUnconditionalTarget(decodeTarget(e.target.value, currentQid, afterIds))}
                  >
                    <option value="">未选</option>
                    {targetOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          {afterQs.length === 0 && (
            <div className="hint hint-bad">当前已是最后一题，无可跳转目标（仅可配置「末尾结束 / 提交无效」）。</div>
          )}
        </div>

        <div className="modal-foot">
          <button className="btn" onClick={onClose}>取消</button>
          <button className="btn btn-primary" onClick={handleConfirm}>确定</button>
        </div>
      </div>
    </div>
  );
}
